/**
 * Where a refresh happened, in the middle of a conversation.
 *
 * Round 5, the owner: "if I refresh mid session, does it reflect on the
 * newest one? How do we indicate that in the UI? What about for different
 * sessions?"
 *
 * The first half was already true and already tested on the server: the next
 * turn always answers against the current analysis, and nothing about a
 * refresh breaks a session. What was missing is saying so. An answer written
 * on Tuesday describes Tuesday's code, and a conversation that scrolls past
 * a refresh without a word looks like one continuous description of one
 * codebase. It is not.
 *
 * Every assistant turn carries `answered_at`: the anchor of the analysis it
 * read (a sha and a snapshot per repo). Two turns whose anchors differ have
 * a refresh between them, and that is where the line goes. The session's
 * `staleness` carries the current anchor and what moved, so the line after
 * the last answer can also say how much.
 *
 * Anchors compare BY VALUE, keyed by repo id: the same anchor arrives as a
 * fresh object on every fetch, so identity would mark a refresh on every
 * poll, and repo order is a server detail nobody should depend on.
 *
 * No request of its own: everything here reads the session list and the
 * session read the app already fetches.
 */

import type { ChatMessage, MapAnchor, RepoMapChange, SessionStaleness } from '../../api/types';
import { plural } from '../../lib/format';

/** True when both anchors describe the same analysis, repo for repo. */
export function sameMapAnchor(
  a: MapAnchor | null | undefined,
  b: MapAnchor | null | undefined,
): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  const left = a.repos ?? [];
  const right = b.repos ?? [];
  if (left.length !== right.length) return false;
  const byId = new Map(right.map((repo) => [repo.repo_id, repo]));
  for (const repo of left) {
    const other = byId.get(repo.repo_id);
    if (other === undefined) return false;
    if ((repo.sha ?? null) !== (other.sha ?? null)) return false;
    if ((repo.snapshot_id ?? null) !== (other.snapshot_id ?? null)) return false;
  }
  return true;
}

/** Sum one field across every changed repo, or null when any repo cannot say. */
function totalOf(changed: readonly RepoMapChange[], field: 'commits' | 'files_changed'): number | null {
  let total = 0;
  for (const change of changed) {
    const value = change[field];
    // git could not say for this repo (a rewritten history, a missing
    // clone). A total that quietly skipped it would understate the change,
    // and an understated number reads as confidently as a right one.
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    total += value;
  }
  return total;
}

/**
 * "14 files, 3 commits" — how much moved, in the two units a person counts
 * in. Null when nothing countable came back, which is the honest answer for
 * a repo whose history git could not walk: the line still says the code
 * changed, it just does not pretend to know by how much.
 */
export function changeSummary(changed: readonly RepoMapChange[]): string | null {
  if (changed.length === 0) return null;
  const files = totalOf(changed, 'files_changed');
  const commits = totalOf(changed, 'commits');
  const parts: string[] = [];
  if (files !== null && files > 0) parts.push(plural(files, 'file'));
  if (commits !== null && commits > 0) parts.push(plural(commits, 'commit'));
  return parts.length > 0 ? parts.join(', ') : null;
}

/** One line across the transcript, saying the code moved at this point. */
export interface RefreshMarker {
  /**
   * The message index this line is drawn ABOVE. `messages.length` means
   * after the last turn: everything you have read is older than the code is
   * now.
   */
  index: number;
  /** "14 files, 3 commits", when the session knows; null when it does not */
  detail: string | null;
}

/**
 * Where the transcript should say the code changed.
 *
 * Two sources, and they answer different questions. BETWEEN two answers, the
 * anchors themselves are the evidence: they differ, so a refresh landed in
 * the gap. We do not know how big it was (the session only carries the diff
 * from its newest answer to now), so that line says less. AFTER the last
 * answer, `staleness` knows exactly what moved and the line can say it.
 *
 * A turn with no anchor at all is a row written before the field existed. It
 * proves nothing either way, so it never draws a line: a refresh we cannot
 * see is better left unclaimed than guessed at.
 */
export function refreshMarkers(
  messages: readonly ChatMessage[],
  staleness: SessionStaleness | null | undefined,
): RefreshMarker[] {
  const markers: RefreshMarker[] = [];

  let previousAnchor: MapAnchor | null = null;
  let previousIndex = -1;
  let newest: MapAnchor | null = null;
  for (let index = 0; index < messages.length; index += 1) {
    const message = messages[index];
    if (message === undefined || message.role !== 'assistant') continue;
    const anchor = message.answered_at ?? null;
    if (anchor === null) continue;
    newest = anchor;
    if (previousAnchor !== null && !sameMapAnchor(previousAnchor, anchor)) {
      // The line belongs directly under the answer it makes old, above the
      // question that was asked after the refresh.
      markers.push({ index: previousIndex + 1, detail: null });
    }
    previousAnchor = anchor;
    previousIndex = index;
  }

  if (staleness !== null && staleness !== undefined && newest !== null) {
    if (!sameMapAnchor(newest, staleness.current)) {
      markers.push({ index: messages.length, detail: changeSummary(staleness.changed ?? []) });
    }
  }
  return markers;
}
